import React, { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { FileText, Calendar, ChevronRight, RefreshCw, Inbox, ArrowLeft } from "lucide-react";


const Subjects = ({subjects, loading}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const subTab = searchParams.get("subTab");

  const borderColorMap = {
    current: "border-blue-500",
    past: "border-green-500",
    upcoming: "border-amber-500",
  };

  const borderColor = borderColorMap[subTab] || "border-green-500";
  
  const handleSubjectPress = useCallback((item) => {
    if (item?.invoiceAmountDue > 0) {
      return;
    }
    navigate(`/dashboard/subjects/${item.subjectId}/summary?programId=${item.enrolmentId}`);//dashboard/subjects/118/summary?programId=93
  }, [navigate]);

  return (
    <div className="">
      {/* Content */}
      <div className="mx-auto px-4 mt-8">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <SubjectSkeleton key={i} />
            ))}
          </div>
        ) : subjects.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {subjects.map((item, idx) => (
              <SubjectCard
                key={idx}
                borderColor={borderColor}
                item={item}
                onPress={() => handleSubjectPress(item)}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <Inbox className="w-12 h-12 mb-3" />
            <p className="text-sm">No Subjects Found</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Subject Card Component
const SubjectCard = ({ item, onPress, borderColor }) => {
  const isLocked = item?.invoiceAmountDue > 0;

  return (
    <div
      onClick={!isLocked ? onPress : undefined}
      className={`bg-white rounded-lg border-l-4 ${borderColor} shadow-md p-4 transition-all
        ${isLocked 
          ? 'opacity-60 cursor-not-allowed' 
          : 'cursor-pointer hover:shadow-xl'
        }`}
    >
      <div className="flex items-start gap-4">
        <div className="flex-1">
          {/* Header Row */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full">
              {item?.subjectCode} 
            </span>

            {item?.subjectType && (
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full
                ${item.subjectType === "Elective" 
                  ? "bg-blue-50 text-blue-600" 
                  : "bg-emerald-50 text-emerald-600"
                }`}
              >
                {item?.subjectType}
              </span>
            )}
          </div>

          {/* Subject Name */}
          <h3 className="text-gray-800 text-base font-semibold mb-3">
            {item?.subjectName}
          </h3>

          {/* Assessments */}
          {(item?.totalAssessmentsAttempted != null && item?.totalAssessments != null) && (
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                <FileText className="w-3.5 h-3.5 text-gray-600" />
              </div>
              <span className="text-gray-600 text-sm">
                {item.totalAssessmentsAttempted}/{item.totalAssessments} assessments
              </span>
            </div>
          )}

          {/* Result Date */}
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
              <Calendar className="w-3.5 h-3.5 text-gray-600" />
            </div>
            <span className="text-gray-600 text-sm">
              {item?.resultDate || "No result date"}
            </span>
          </div>
        </div>

        {/* Arrow */}
        {!isLocked && (
          <ChevronRight className="w-5 h-5 text-gray-400 shrink-0 mt-1" />
        )}
      </div>
    </div>
  );
};

// Skeleton Component
const SubjectSkeleton = () => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
      <div className="flex justify-between mb-3">
        <div className="h-6 w-20 bg-gray-200 rounded-full"></div>
        <div className="h-6 w-16 bg-gray-200 rounded-full"></div>
      </div>
      <div className="h-5 w-full bg-gray-200 rounded mb-3"></div>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-6 h-6 bg-gray-200 rounded-full"></div>
        <div className="h-4 w-32 bg-gray-200 rounded"></div>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 bg-gray-200 rounded-full"></div>
        <div className="h-4 w-24 bg-gray-200 rounded"></div>
      </div>
    </div>
  );
};

export default Subjects;